import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { dogs as staticDogs } from '../data/dogs';
import { fetchDogs } from '../lib/api';
import { useSupabaseData } from '../lib/useSupabaseData';
import { img } from '../lib/img';
import { useTheme } from '../lib/theme';
import { Reveal, RevealHeading } from '../components/Reveal';

const DogCard = ({ name, id, profileImage }) => {
  const { isImperial } = useTheme();

  if (isImperial) {
    // Imperial: portretowa karta z gradientem, złotą linią i subtelnym zbliżeniem
    return (
      <Link to={`/nasze-psy/${id}`} className="group w-full sm:w-1/2 lg:w-1/3 px-3">
        <motion.div layoutId={`dog-image-${id}`} className="relative aspect-[3/4] overflow-hidden bg-brand-bg-side">
          <img
            src={img(profileImage, { w: 700 })}
            alt={name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-[#0a0908]/25 to-transparent" aria-hidden />
          <div className="absolute inset-0 border border-brand-accent/0 group-hover:border-brand-accent/50 transition-colors duration-500" aria-hidden />
          <div className="absolute inset-x-0 bottom-0 p-5">
            <div className="h-px w-10 bg-brand-accent mb-3 transition-all duration-500 group-hover:w-20" aria-hidden />
            <h3 className="font-display text-3xl text-brand-text-light">{name}</h3>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to={`/nasze-psy/${id}`} className="text-center flex flex-col items-center group w-full sm:w-1/2 lg:w-1/3 px-2">
      <motion.div
        layoutId={`dog-image-${id}`}
        className="w-56 h-56 rounded-full bg-brand-bg-side shadow-lg transition-transform duration-300 group-hover:scale-105 overflow-hidden"
      >
        <img src={img(profileImage, { w: 450 })} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </motion.div>
      <h3 className="text-2xl mt-4 text-brand-text-light group-hover:text-brand-accent transition-colors">{name}</h3>
    </Link>
  );
};

const SectionTitle = ({ children, isImperial }) =>
  isImperial ? (
    <div className="text-center mb-12">
      <RevealHeading as="h2" text={children} className="text-4xl md:text-5xl text-brand-accent" />
      <Reveal y={0} delay={0.2} className="h-px w-16 mx-auto mt-4 bg-gradient-to-r from-transparent via-brand-accent to-transparent" aria-hidden />
    </div>
  ) : (
    <RevealHeading as="h2" text={children} className="text-3xl md:text-4xl text-center mb-12 text-brand-accent" />
  );

const OurDogsPage = () => {
  const { t } = useTranslation();
  const { isImperial } = useTheme();
  const { data: dogs } = useSupabaseData(fetchDogs, staticDogs, 'dogs');

  const females = dogs.filter(d => d.category === 'females');
  const males = dogs.filter(d => d.category === 'males');
  const inMemory = dogs.filter(d => d.category === 'memory');

  const grid = `flex flex-wrap justify-center ${isImperial ? 'gap-y-8' : 'gap-y-16'}`;

  return (
    <div className="animate-fadeIn">
      <section className="my-12 md:my-20 text-center">
        <RevealHeading as="h1" text={t('ourDogs.title')} className="text-4xl md:text-5xl text-brand-accent" />
        <Reveal as="p" delay={0.18} className="mt-4 text-lg md:text-xl text-brand-text-light/80 max-w-2xl mx-auto">
          {t('ourDogs.subtitle')}
        </Reveal>
      </section>

      {/* Suki */}
      <section className="my-16 md:my-24">
        <SectionTitle isImperial={isImperial}>{t('ourDogs.females_section_title')}</SectionTitle>
        <div className={grid}>
          {females.map(dog => <DogCard key={dog.id} {...dog} />)}
        </div>
      </section>

      {/* Psy */}
      <section className="my-16 md:my-24">
        <SectionTitle isImperial={isImperial}>{t('ourDogs.males_section_title')}</SectionTitle>
        <div className={grid}>
          {males.map(dog => <DogCard key={dog.id} {...dog} />)}
        </div>
      </section>

      {/* Na zawsze z nami */}
      <section className="my-16 md:my-24">
        <SectionTitle isImperial={isImperial}>{t('ourDogs.youth_section_title')}</SectionTitle>
        <div className={grid}>
          {inMemory.map(dog => <DogCard key={dog.id} {...dog} />)}
        </div>
      </section>
    </div>
  );
};

export default OurDogsPage;
